import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  Observable,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { Contact } from 'src/app/core/models/contact/contact.interface';
import { ContactFormModel } from 'src/app/core/models/contact/contact-form.model';
import { MinimalOrganization } from 'src/app/core/models/organization/minimal-organization.interface';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { AuthService } from 'src/app/core/services/auth.service';
import { ContactService } from 'src/app/core/services/contact.service';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { CoreModule } from 'src/app/core/core.module';
import { EditableContact } from '../editable-contact';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbHighlight } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-create-contact',
  imports: [CommonModule, CoreModule, NgbHighlight, ReactiveFormsModule, TranslateModule],
  templateUrl: './create-contact.component.html',
  styleUrl: './create-contact.component.scss',
  providers: [ContactFormModel],
  standalone: true,
})
export class CreateContactComponent
  extends EditableContact
  implements OnInit, OnDestroy
{
  formModel = inject(ContactFormModel);
  private readonly employeeService = inject(EmployeeService);
  private readonly organizationService = inject(OrganizationService);
  private readonly authService = inject(AuthService);
  private readonly contactService = inject(ContactService);

  @Input({ required: true })
  public set companyId(value: string) {
    this._companyId = value;
  }

  @Input() public isShownOrganization = true;
  @Output() public cancelled = new EventEmitter<void>();
  @Output() public created = new EventEmitter<Contact>();

  public defaultResponsibleUser?: MinimalProfile;
  public companyEmployees$ = new Observable<MinimalProfile[]>();
  public companyOrganizations$ = new Observable<MinimalOrganization[]>();
  public isSubmitting = false;

  private _companyId = '';
  private unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.employeeService.setEmployeesUrl(this._companyId);
    this.organizationService.setOrganizationsUrl(this._companyId);
    this.contactService.setContactsUrl(this._companyId);

    this.companyEmployees$ = this.employeeService.getCompanyEmployeesLookup();

    this.companyEmployees$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(x => x.length > 0),
        tap(employees => {
          const userData = this.authService.user$.getValue().data!;

          this.defaultResponsibleUser = employees.find(
            employee => employee.id === userData.id,
          );

          this.formModel.form.patchValue({
            responsibleUserId: this.defaultResponsibleUser?.id,
          });
        }),
      )
      .subscribe();

    this.companyOrganizations$ = this.organizationService.getMinimalOrganizations();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public submitForm(): void {
    if (this.formModel.form.invalid) {
      this.formModel.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    this.contactService
      .createContact(this.formModel.form.getRawValue())
      .pipe(
        tap(contact => this.created.emit(contact)),
        catchError(() => EMPTY),
        finalize(() => (this.isSubmitting = false)),
      )
      .subscribe();
  }
}
